import { dateFromNumber } from "@nova/serializer/src/client/util"
import {
    LiveVideoStatus,
    selectLiveVideoFlightById,
    selectLiveVideoFlightIdsOrderedByDateCreated,
    selectLiveVideoStatus,
} from "features/entity-caching/flights/selectors"
import { droneIconColors } from "features/map/layers/video/VideoDroneLayer"
import { zoomToShowPoints } from "features/map/zoom/thunks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { fetchTelemetryEvents } from "tools/telemetry/fetchTelemetryEvents"
import store from "integrations/redux/store"
import { AbstractVideoController, VideoControllerData } from "./AbstractVideoController"
import Hls, { ErrorDetails, Events } from "hls.js"
import {
    flightListenerEventEmitter,
    startListeningToFlightChanges,
} from "./hooks/flightListenerMiddleware"
import { VideoControllerFactory } from "./VideoControllerFactory"
import { fixBadTelemetryData } from "../module"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"

export class LiveVideoFlightRecordedController extends AbstractVideoController {
    private videoIsInitialized = false
    private hls?: Hls
    private waitingToStart = false
    private waitingForRecording = false
    private triedVideoOnlyPlaylist = false

    constructor({ flightId }: { flightId: string }) {
        super({ flightId })
        const orderedLiveFlightIds = selectLiveVideoFlightIdsOrderedByDateCreated(store.getState())
        const thisFlightIndex = orderedLiveFlightIds.findIndex((flight) => flight.id === flightId)
        if (thisFlightIndex === -1) {
            throw new Error("Initialized LiveVideoController for live flight that does not exist")
        }
        const flight = selectLiveVideoFlightById(flightId)(store.getState())
        this.waitingToStart = !flight.startedAt
        this.waitingForRecording =
            !this.waitingToStart && !flight.live && flight.saveRecording && !flight.recordingPrefix
        this.droneIconColor = droneIconColors[thisFlightIndex % droneIconColors.length]
        this.stopListeningToFlightChanges = startListeningToFlightChanges({
            flightId: this.flightId,
        })
        this.boundHandleFlightChange = this.handleFlightChange.bind(this)
        flightListenerEventEmitter.on(this.flightId, this.boundHandleFlightChange)
    }

    override async initialize() {
        const setup = () => {
            if (!this.videoIsInitialized && !this.waitingToStart && !this.waitingForRecording) {
                this.off("videoAvailabilityChanged", setup)
                // oxlint-disable-next-line import/no-named-as-default-member
                if (Hls.isSupported()) {
                    const flight = selectLiveVideoFlightById(this.flightId)(store.getState())
                    const hls = new Hls({
                        xhrSetup: (xhr) => {
                            xhr.withCredentials = true
                        },
                    })
                    Promise.all([
                        fileManagementApi.getDownloadUrl(`${flight.recordingPrefix}_master.m3u8`, {
                            bucket: "agora",
                        }),
                        fileManagementApi.getDownloadUrl(`${flight.recordingPrefix}.m3u8`, {
                            bucket: "agora",
                        }),
                    ]).then(([masterUrl, videoOnlyUrl]) => {
                        // Master 404s for recordings with no audio rendition; fall back to video-only.
                        hls.on(Events.ERROR, (_event, data) => {
                            if (
                                data.details === ErrorDetails.MANIFEST_LOAD_ERROR &&
                                !this.triedVideoOnlyPlaylist
                            ) {
                                this.triedVideoOnlyPlaylist = true
                                hls.loadSource(videoOnlyUrl)
                            }
                        })
                        hls.loadSource(masterUrl)
                        this.hls = hls
                        this.emit("videoTrackChanged")
                        this.videoIsInitialized = true
                    })
                } else {
                    this.videoIsInitialized = true
                }
            }
        }
        this.on("videoAvailabilityChanged", setup)
        setup()
    }
    override async deinitialize() {
        this.stopListeningToFlightChanges?.()
        flightListenerEventEmitter.off(this.flightId, this.boundHandleFlightChange!)
        if (this.videoIsInitialized) {
            this.hls?.stopLoad()
            this.videoIsInitialized = false
        }
    }

    private boundHandleFlightChange?: typeof this.handleFlightChange
    private handleFlightChange() {
        const flight = selectLiveVideoFlightById(this.flightId)(store.getState())
        if (!flight) return
        const isWaiting = !flight.live && flight.saveRecording && !flight.recordingPrefix
        if (isWaiting !== this.waitingForRecording) {
            this.waitingForRecording = isWaiting
            this.emit("videoAvailabilityChanged")
        }
        if (flight.live) {
            VideoControllerFactory.tearDownControllerForFlight({ flightId: this.flightId })
        }
    }

    private detach: () => void = () => {}
    private rAFId?: number
    override attachToElement(element: HTMLVideoElement) {
        this.videoElement = element
        const attach = () => {
            if (this.hls) {
                element.controls = true
                const updateProgress = () => {
                    const progress = element.duration ? element.currentTime / element.duration : 0
                    if (this.videoProgress !== progress) {
                        this.videoProgress = progress
                        this.emit("videoProgressChanged")
                    }
                    this.rAFId = requestAnimationFrame(updateProgress)
                }
                const startLoop = () => {
                    if (!this.rAFId) {
                        this.rAFId = requestAnimationFrame(updateProgress)
                    }
                }
                const stopLoop = () => {
                    if (this.rAFId) {
                        cancelAnimationFrame(this.rAFId)
                        this.rAFId = undefined
                    }
                }
                const handlePlay = () => {
                    this.isPlaying = true
                    this.emit("isPlayingChanged")
                    startLoop()
                }
                const handlePause = () => {
                    this.isPlaying = false
                    this.emit("isPlayingChanged")
                    stopLoop()
                }
                element.addEventListener("play", handlePlay)
                element.addEventListener("pause", handlePause)
                this.hls.attachMedia(element)
                this.isPlaying = true
                this.hasPlayed = true
                this.emit("isPlayingChanged")
                this.detach = () => {
                    stopLoop()
                    element?.removeEventListener("play", handlePlay)
                    element?.removeEventListener("pause", handlePause)
                    this.hls?.detachMedia()
                }
            }
        }
        this.on("videoTrackChanged", attach)
        attach()
        return () => {
            this.off("videoTrackChanged", attach)
            this.detach()
        }
    }

    override async fetchAllData(zoomTo?: true) {
        const state = store.getState()
        const flight = selectLiveVideoFlightById(this.flightId)(state)
        const orgId = selectActiveWorkspaceId()(state)
        const projectId = selectActivatedFireId(state)
        const { events } = await fetchTelemetryEvents({
            orgId: orgId!,
            projectId: projectId!,
            id: this.flightId,
            start: dateFromNumber(flight.startedAt).getTime(),
            end: flight.endedAt ? dateFromNumber(flight.endedAt).getTime() : undefined,
        })
        const videoEvents = events.filter((e): e is VideoControllerData => e.type === "liveVideo")
        const sorted = [...videoEvents, ...this.data].sort((a, b) => a.timestamp - b.timestamp)
        const filtered = sorted.filter((e, i) => e.timestamp !== sorted?.[i - 1]?.timestamp)
        const fixed = fixBadTelemetryData(filtered)
        this.data = fixed
        this.hasFetched = true
        this.emit("dataChanged")
        if (zoomTo) {
            store.dispatch(
                zoomToShowPoints({
                    points: fixed.map((e) => [e.longitude, e.latitude]),
                })
            )
        }
    }

    isWaitingForRecording() {
        const flight = selectLiveVideoFlightById(this.flightId)(store.getState())
        return !!flight && selectLiveVideoStatus(flight) === LiveVideoStatus.AwaitingRecording
    }
}
